import React from 'react';
import { clsx } from 'clsx';

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}

export const PageHeader: React.FC<PageHeaderProps> = ({ title, subtitle, actions }) => (
  <header className="rounded-2xl border border-slate-200/80 bg-white px-6 py-5 shadow-soft overflow-hidden relative">
    {/* Línea de acento superior */}
    <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-primary-500 via-primary-400 to-primary-600 rounded-t-2xl" />
    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  </header>
);

interface DataToolbarProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  leftSlot?: React.ReactNode;
  rightSlot?: React.ReactNode;
}

export const DataToolbar: React.FC<DataToolbarProps> = ({
  searchTerm,
  onSearchChange,
  searchPlaceholder = 'Buscar...',
  leftSlot,
  rightSlot,
}) => (
  <div className="rounded-xl border border-slate-200/80 bg-white/60 backdrop-blur-sm p-4 shadow-soft">
    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex flex-1 items-center gap-3">
        <div className="relative w-full max-w-lg">
          <input
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-800 placeholder:text-slate-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200 transition-colors duration-150"
          />
        </div>
        {leftSlot}
      </div>
      {rightSlot}
    </div>
  </div>
);

interface StatusBadgeProps {
  tone: 'success' | 'danger' | 'warning' | 'neutral' | 'info';
  children: React.ReactNode;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({ tone, children }) => {
  const tones = {
    success: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    danger: 'bg-rose-50 text-rose-700 ring-rose-200',
    warning: 'bg-amber-50 text-amber-700 ring-amber-200',
    neutral: 'bg-slate-100 text-slate-700 ring-slate-200',
    info: 'bg-blue-50 text-blue-700 ring-blue-200',
  };

  return (
    <span className={clsx('inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1', tones[tone])}>
      {children}
    </span>
  );
};

interface ActionIconButtonProps {
  title: string;
  onClick: () => void;
  tone?: 'default' | 'danger' | 'success' | 'info' | 'neutral';
  children: React.ReactNode;
  disabled?: boolean;
}

export const ActionIconButton: React.FC<ActionIconButtonProps> = ({ title, onClick, tone = 'default', children, disabled = false }) => {
  const tones = {
    default: 'text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus-visible:ring-slate-300',
    neutral: 'text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus-visible:ring-slate-300',
    danger:  'text-rose-500 hover:bg-rose-50 hover:text-rose-700 focus-visible:ring-rose-300',
    success: 'text-emerald-600 hover:bg-emerald-50 hover:text-emerald-800 focus-visible:ring-emerald-300',
    info:    'text-primary-600 hover:bg-primary-50 hover:text-primary-700 focus-visible:ring-primary-300',
  };

  return (
    <div className="group relative inline-flex">
      <button
        type="button"
        onClick={onClick}
        aria-label={title}
        disabled={disabled}
        className={clsx(
          'inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors duration-150 focus:outline-none focus-visible:ring-2 disabled:opacity-40 disabled:cursor-not-allowed',
          tones[tone]
        )}
      >
        {children}
      </button>
      <span className="pointer-events-none absolute -top-8 left-1/2 z-20 -translate-x-1/2 rounded-md bg-slate-900 px-2 py-1 text-[11px] font-medium text-white opacity-0 transition-opacity duration-150 group-hover:opacity-100">
        {title}
      </span>
    </div>
  );
};

export const DataTableCard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <section className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-soft">{children}</section>
);

export const EmptyState: React.FC<{ title: string; description: string }> = ({ title, description }) => (
  <div className="py-14 text-center">
    <div className="mx-auto mb-4 h-12 w-12 rounded-xl bg-slate-100" />
    <p className="text-sm font-semibold text-slate-800">{title}</p>
    <p className="mt-1 text-sm text-slate-500">{description}</p>
  </div>
);
